import * as React from "react";

import { DataTable, Page } from "src/components/common";
import { Button } from "src/components/buttons";

import OrganizationAPI from "../API/OrganizationAPI";

import {
	filterByStatus,
	StatusFilterMethod,
	setStatusCellValue,
} from "src/components/forms/StatusFilter";
import {
	ClearFilterButton,
	ActionIcon,
} from "src/components/forms/ActionColumnIconsAndButton";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { Checkbox, Tooltip, DatePicker } from "antd";
import DatasetAPI from "../Datasets/DatasetAPI";
import DatasetModal from "./OrganizationUsersDatasetModal";
import {
	BulkActivateUsers,
	BulkAddRoles,
} from "src/@types/ImpiricaPlatformApi";
import moment from "moment";
import { Icon } from "src/components/icons/Icon";
import { Monitor, Smartphone, User, UserLock } from "lucide-react";
import { rowDateSortFunction } from "src/common/utils";
import ActivateUsersModal from "./OrganizationUsersActivateModal";
import { ApiClients } from "src/Api/useApi";
import BulkAddTrainingCoursesModal from "./OrganizationUsersTrainingModal";
import { toast } from "react-toastify";
interface ISelectableUser extends OrganizationUserDTO {
	selected: boolean;
	name: string;
}

interface IProps {
	match?: any;
	history?: any;
	api?: ApiClients;
}

interface IState {
	users: ISelectableUser[];
	roles: RoleDTO[];
	organization: OrganizationDTO;
	filtered: any[];
	loading: boolean;
	allSelected: boolean;
	openModal: boolean;
	openActivateModal: boolean;
	openBulkTrainingModal: boolean;
	datasetList: DatasetDTO[];
	datasetRoleItemList: DatasetRoleItemDTO[];
	datasetRoleItem: DatasetRoleItemDTO;
	errorMsg: string;
	user: OrganizationUserDTO;
}

interface IUrlParams {
	organizationId: number;
	userId: number;
}

const defaultOgranization: OrganizationDTO = {
	name: "",
};

class OrganizationUsers extends React.Component<IProps, IState> {
	public organizationId: string | number =
		this.props.match.params.organizationId;
	public _isMounted = true;
	private url: IUrlParams;
	public constructor(props: any) {
		super(props);
		this.state = {
			users: [],
			roles: [],
			organization: defaultOgranization,
			filtered: [],
			loading: true,
			allSelected: false,
			openModal: false,
			openActivateModal: false,
			openBulkTrainingModal: false,
			datasetList: [],
			datasetRoleItemList: [],
			datasetRoleItem: {},
			errorMsg: "",
			user: {},
		};
		this.url = {
			organizationId: this.props.match.params.organizationId,
			userId: Number(this.props.match.params.userId),
		};
	}

	public componentDidMount() {
		const { organizationId } = this.props.match.params;
		OrganizationAPI.getOrganizationUsers({ id: organizationId })
			.then((usersDto) => {
				if (this._isMounted) {
					const newUsers = usersDto.map<ISelectableUser>((dto) => ({
						...dto,
						name: dto.isPrimaryOrg
							? `${dto.firstName} ${dto.lastName}`
							: `${dto.firstName} ${dto.lastName} - EXTERNAL USER`,
						selected: false,
					}));
					this.setState({ users: newUsers });
				}
			})
			.then(() => {
				OrganizationAPI.getOrganizationRoles({ id: organizationId })
					.then((res) => {
						if (this._isMounted) {
							this.setState({ roles: res });
						}
					})
					.then(() => {
						OrganizationAPI.getOrganization({ id: organizationId })
							.then((org) => {
								this.setState({ organization: org });
							})
							.then(() => {
								DatasetAPI.getOrganizationDatasets(
									this.url.organizationId
								).then((datasets) => {
									this.setState({ datasetList: datasets, loading: false });
								});
							});
					});
			});
		this.setState({ filtered: [{ id: "isActive", value: "Active" }] });
	}

	public componentWillUnmount() {
		this._isMounted = false;
	}

	public render() {
		const columns = this.getColumns();
		const { organization } = this.state;
		const selectedUsers = this.getSelectedUsers();

		return (
			<Page title={organization.isDefault === true ? "Admin users" : "Users"}>
				<div className="container-fluid">
					<AuthorizationWrapper
						type="hide"
						permissions={[AdminPermissions.EditDatasetUser]}
					>
						<div className="d-flex flex-column mb-4">
							<div className="ml-3">
								<p className="text-danger font-weight-bold text-wrap">
									IMPORTANT: If this is part of a multi-org configuration,
									please update the other organization(s) accordingly!
								</p>
							</div>
							<div className="ml-auto mb-3">
								<Button
									className="btn-primary mr-2"
									disabled={this.isDisabled()}
									onClick={this.openBulkTrainingModal}
								>
									Add Training Courses
								</Button>
								<Button
									className="btn-primary mr-2"
									disabled={this.isDisabled()}
									onClick={this.openActivateModal}
								>
									Activate/Inactivate Users
								</Button>
								<Button
									disabled={this.isDisabled()}
									className="btn-primary mr-2"
									onClick={
										this.state.openModal ? this.closeModal : this.openModal
									}
								>
									Add Dataset Roles
								</Button>
								<Button
									to={this.getActionLink(this.organizationId, -1)}
									className="btn-primary"
									disabled={this.state.organization.isActive === false}
								>
									New User
								</Button>
							</div>
						</div>
					</AuthorizationWrapper>
					<DataTable
						data={this.state.users}
						columns={columns}
						showPagination={true}
						onFilteredChange={(filtered: any) => this.setState({ filtered })}
						filterable={true}
						filtered={this.state.filtered}
						onRowClick={this.onRowClick}
						loading={this.state.loading}
						storageKey={"OrgUsersGrid"}
					/>
					<DatasetModal
						openModal={this.state.openModal}
						selectedUsers={selectedUsers}
						datasetList={this.state.datasetList}
						roles={this.state.roles}
						datasetRoleItemList={this.state.datasetRoleItemList}
						datasetRoleItem={this.state.datasetRoleItem}
						closeModal={this.closeModal}
						onRoleChanged={this.onRoleChanged}
						onDatasetChanged={this.onDatasetChanged}
						onAddRoleItem={this.onAddRoleItem}
						onRemoveRoleItem={this.onRemoveRoleItem}
						errorMsg={this.state.errorMsg}
						onSave={this.onSave}
						loading={this.state.loading}
					/>
					<ActivateUsersModal
						openActivateModal={this.state.openActivateModal}
						closeActivateModal={this.closeModal}
						onSave={this.onActivateSave}
					/>
					<BulkAddTrainingCoursesModal
						openBulkTrainingCoursesModal={this.state.openBulkTrainingModal}
						closeBulkTrainingCoursesModal={this.closeModal}
						users={selectedUsers.map((user) => user.id as number)}
						orgId={Number(this.organizationId)}
					/>
				</div>
			</Page>
		);
	}

	private getSelectedUsers() {
		return this.state.users.filter((user) => user.selected === true);
	}

	private getActionLink = (orgId: any, rowId: any) =>
		`/admin/organizations/${orgId}/users/${rowId}/details`;

	private onRowClick = ({ original }: { original?: any }) => {
		if (original.isPrimaryOrg) {
			this.props.history.push(
				this.getActionLink(this.props.match.params.organizationId, original.id)
			);
		}
	};

	private readDatasetRoles = (datasetRoleNames: string[]) => {
		let datasetText = "";
		datasetRoleNames.forEach((name) => {
			datasetText += name + "\n";
		});
		return datasetText;
	};

	private onSelectAll = () => {
		let allSelected = !this.state.allSelected;
		let tempList = this.state.users;
		tempList = tempList.map((user) => ({
			...user,
			selected: allSelected,
		}));

		this.setState({ users: tempList, allSelected: allSelected });
	};

	private onSelectRow = (id: number) => {
		let tempList = this.state.users;
		const selectedUser = tempList.find((user) => user.id === id);
		if (!selectedUser) return;
		selectedUser.selected = !selectedUser.selected;
		this.setState({ users: tempList });
	};

	private isDisabled = () => {
		const users = this.state.users;
		const org = this.state.organization;
		const disable =
			users.find((user) => user.selected === true) && org.isActive
				? false
				: true;
		return disable;
	};

	private closeModal = () => {
		this.setState({
			openModal: false,
			openActivateModal: false,
			openBulkTrainingModal: false,
			datasetRoleItemList: [],
			errorMsg: "",
		});
	};

	private openModal = () => {
		this.setState({ openModal: true, datasetRoleItem: {} });
	};

	private openActivateModal = () => {
		this.setState({ openActivateModal: true });
	};

	private openBulkTrainingModal = () => {
		this.setState({ openBulkTrainingModal: true });
	};

	private onRoleChanged = (value: any) => {
		const datasetRoleItem = { ...this.state.datasetRoleItem };
		datasetRoleItem.roleId = value;
		this.setState({ datasetRoleItem: datasetRoleItem });
	};

	private onDatasetChanged = (value: any) => {
		const datasetRoleItem = { ...this.state.datasetRoleItem };
		datasetRoleItem.datasetId = value;
		this.setState({ datasetRoleItem });
	};

	private onAddRoleItem = () => {
		const datasetRoleItem = this.state.datasetRoleItem;
		const datasetRoleItems = [...this.state.datasetRoleItemList];
		this.setState({ errorMsg: "" });

		if (
			datasetRoleItem.roleId === undefined ||
			datasetRoleItem.datasetId === undefined
		) {
			this.setState({ errorMsg: "Please select a role and dataset" });
			return;
		}
		if (
			datasetRoleItems.find(
				(item) => item.datasetId === datasetRoleItem.datasetId
			)
		) {
			this.setState({
				errorMsg: "This dataset already has a role assigned to it",
			});
			return;
		}
		if (
			!datasetRoleItems.find(
				(item) =>
					item.datasetId === datasetRoleItem.datasetId &&
					item.roleId === datasetRoleItem.roleId &&
					this.state.errorMsg === ""
			)
		) {
			datasetRoleItems.push(datasetRoleItem);
			this.setState({ datasetRoleItemList: datasetRoleItems });
		} else {
			this.setState({ errorMsg: "Dateset role already exists" });
		}
	};

	private onRemoveRoleItem = (datasetRoleItem: DatasetRoleItemDTO) => {
		let datasetRoles = [...this.state.datasetRoleItemList];
		if (datasetRoles !== undefined) {
			datasetRoles = datasetRoles.filter(
				(item) =>
					!(
						item.datasetId === datasetRoleItem.datasetId &&
						item.roleId === datasetRoleItem.roleId
					)
			);
			this.setState({ datasetRoleItemList: datasetRoles, errorMsg: "" });
		}
	};

	public onActivateSave = async (active: boolean) => {
		if (!this.props.api) throw new Error("API not initialized");

		const selectedUsers = this.getSelectedUsers();
		let userIds = selectedUsers.map((user) => user.id as number);
		let activations = new BulkActivateUsers({ isActive: active, userIds });

		this.setState({ loading: true });
		try {
			const { usersClient } = this.props.api;
			if (!usersClient) throw new Error("Users client not initialized");
			const result = await usersClient.bulkActivateUsers(
				this.url.organizationId,
				activations
			);

			const updatedUsers = [...this.state.users];
			updatedUsers.forEach((user) => {
				user.selected = false;
				if (result.includes(user.id as number)) {
					user.isActive = active;
				}
			});
			this.setState({
				loading: false,
				openActivateModal: false,
				users: updatedUsers,
				allSelected: false,
			});

			//if any result is false, then show warning
			if (result) {
				toast.success("Users activated or inactivated");
			} else {
				toast.error("Failed to activate or inactivate users");
			}
		} catch {
			toast.error("Failed to activate or inactivate users");
			this.setState({ loading: false });
		}
	};

	private onSave = async () => {
		if (!this.props.api) throw new Error("API not initialized");
		let additions = new Array<BulkAddRoles>();
		const selectedUsers = this.getSelectedUsers();
		let datasetRoles = this.state.datasetRoleItemList.map((dsr) => ({
			datasetId: dsr.datasetId as number,
			roleId: dsr.roleId as number,
		}));
		let userIds = selectedUsers.map((user) => user.id as number);
		this.setState({ loading: true });
		userIds.forEach((userId) => {
			datasetRoles.forEach((dsr) =>
				additions.push(new BulkAddRoles({ userId, ...dsr }))
			);
		});
		try {
			const { usersClient } = this.props.api;
			if (!usersClient) throw new Error("Users client not initialized");
			await usersClient.bulkAddRoles(this.url.organizationId, additions);
			OrganizationAPI.getOrganizationUsers({
				id: this.url.organizationId,
			}).then((usersDto) => {
				if (this._isMounted) {
					const newUsers = usersDto.map<ISelectableUser>((dto) => ({
						...dto,
						name: `${dto.firstName} ${dto.lastName}`,
						selected: false,
					}));
					this.setState({
						loading: false,
						users: newUsers,
						allSelected: false,
						openModal: false,
						datasetRoleItemList: [],
						errorMsg: "",
						datasetRoleItem: {},
					});
					toast.success("Dataset Roles saved");
				}
			});
		} catch {
			toast.error("Failed to add dataset roles");
			this.setState({ loading: false });
		}
	};

	private returnDeviceComponent(row: any) {
		const value = row.value;
		if (value === true) {
			return (
				<Tooltip title="Mobile">
					<Icon icon={Smartphone} />
				</Tooltip>
			);
		} else if (value === false) {
			return (
				<Tooltip title="Desktop">
					<Icon icon={Monitor} />
				</Tooltip>
			);
		} else {
			return <></>;
		}
	}

	private returnUserComponent(row: any) {
		const name = <p>{`${row.original.firstName} ${row.original.lastName}`}</p>;
		if (row.original.isPrimaryOrg === true) {
			return (
				<div style={{ display: "flex" }}>
					<Tooltip title="User">
						<Icon style={{ marginTop: 3 }} icon={User} />
					</Tooltip>
					{name}
				</div>
			);
		} else if (row.original.isPrimaryOrg === false) {
			return (
				<div style={{ display: "flex" }}>
					<Tooltip title="External">
						<Icon
							style={{ marginTop: 3 }}
							color="#FF5C39"
							icon={UserLock}
						/>
					</Tooltip>
					{name}
				</div>
			);
		} else {
			return <></>;
		}
	}

	private lastLoginFilter(filter: any, onChange: any) {
		return (
			<DatePicker
				onChange={onChange}
				allowClear={true}
				style={{ width: "100%" }}
				format="MMM DD, YYYY"
				value={filter ? filter.value : undefined}
			/>
		);
	}

	private selectedRowCheckbox(row: any) {
		return (
			<div
				style={{ width: "100%", height: "100%" }}
				onClick={(event) => {
					event.stopPropagation();
					this.onSelectRow(row.original.id);
				}}
			>
				<Checkbox
					checked={row.value}
					onClick={(event) => event.stopPropagation()}
					onChange={() => this.onSelectRow(row.original.id as number)}
				/>
			</div>
		);
	}

	private getColumns() {
		const columns = [
			{
				Header: (
					<Checkbox
						checked={this.state.allSelected}
						onChange={this.onSelectAll}
					/>
				),
				accessor: "selected",
				sortable: false,
				filterable: false,
				width: 50,
				Cell: (row: any) => this.selectedRowCheckbox(row),
			},
			{
				Header: <Tooltip title="Name">Name</Tooltip>,
				accessor: "name",
				filterable: true,
				Cell: (row: any) => this.returnUserComponent(row),
			},
			{
				Header: <Tooltip title="Active">Active</Tooltip>,
				accessor: "isActive",
				filterMethod: filterByStatus("isActive"),
				Filter: StatusFilterMethod,
				Cell: setStatusCellValue,
				maxWidth: 130,
			},
			{
				Header: <Tooltip title="Email">Email</Tooltip>,
				accessor: "email",
				filterable: true,
			},
			{
				Header: <Tooltip title="Dataset Roles">Dataset Role</Tooltip>,
				accessor: "datasetRoleNames",
				filterable: true,
				Cell: (row: any) => {
					if (row.value === undefined || row.value.length === 0) {
						return "None";
					}
					return this.readDatasetRoles(row.value);
				},
				style: { whiteSpace: "pre" },
			},
			{
				id: "lastLogin",
				Header: <Tooltip title="Last Login">Last Login</Tooltip>,
				filterable: true,
				accessor: (u: any) => {
					if (u.lastLogin) {
						return moment
							.utc(u.lastLogin)
							.local()
							.format("MMM DD, YYYY h:mm A");
					} else {
						return "";
					}
				},
				sortMethod: rowDateSortFunction,
				filterMethod: (filter: any, rowData: any) =>
					filter.value !== null
						? moment.utc(rowData.lastLogin).local().format("MMM DD, YYYY") ===
						  filter.value.format("MMM DD, YYYY")
						: " ",
				Filter: ({ filter, onChange }: { filter: any; onChange: any }) =>
					this.lastLoginFilter(filter, onChange),
			},
			{
				Header: <Tooltip title="Device">Device</Tooltip>,
				filterable: false,
				accessor: "lastLoginMobile",
				Cell: (row: any) => this.returnDeviceComponent(row),
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: <ActionIcon title="Details" type="open" />,
				Filter: (
					<ClearFilterButton
						onClick={() =>
							this.setState({ filtered: [{ id: "isActive", value: "Active" }] })
						}
					/>
				),
				id: "Actions",
			},
		];
		return columns;
	}
}
export default OrganizationUsers;
